import { supabase } from '../lib/supabase';
import { db, auth } from '../lib/firebase';
import { collection, onSnapshot, query, orderBy, limit, getDocs, doc } from 'firebase/firestore';
import { RealtimeChannel } from '@supabase/supabase-js';

type UpdateType = 'INSERT' | 'UPDATE' | 'DELETE' | 'added' | 'modified' | 'removed' | 'SYNC_COMPLETE' | 'CONNECTION_STATUS';
type DataSource = 'supabase' | 'firebase';

interface RealtimeUpdate {
  type: UpdateType;
  source?: DataSource;
  data: any;
}

type Subscriber = (update: RealtimeUpdate) => void;
type UnsubscribeFunction = () => void;

class RealtimeService {
  private subscribers: Map<string, Set<Subscriber>> = new Map();
  private supabaseChannels: Map<string, RealtimeChannel> = new Map();
  private firebaseUnsubscribers: Map<string, UnsubscribeFunction> = new Map();
  private syncInProgress: boolean = false;
  private connectionStatus = {
    supabase: false,
    firebase: false
  };
  private statusSubscribers: Set<Subscriber> = new Set();
  private initialized: boolean = false;
  private retryAttempts: number = 3;
  private retryDelay: number = 1000;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      await this.setupConnections();
    } catch (error) {
      console.error('Initialization error:', error);
      // Retry initialization
      setTimeout(() => this.initialize(), this.retryDelay);
    }
  }

  private async setupConnections() {
    // Setup Supabase connection
    const channel = supabase.channel('system')
      .on('presence', { event: 'sync' }, () => {
        this.connectionStatus.supabase = true;
        this.notifyStatusChange();
      })
      .on('presence', { event: 'join' }, () => {
        this.connectionStatus.supabase = true;
        this.notifyStatusChange();
      })
      .on('presence', { event: 'leave' }, () => {
        this.connectionStatus.supabase = false;
        this.notifyStatusChange();
      })
      .subscribe(async (status) => {
        this.connectionStatus.supabase = status === 'SUBSCRIBED';
        this.notifyStatusChange();
      });

    // Monitor Firebase auth state
    auth.onAuthStateChanged((user) => {
      this.connectionStatus.firebase = !!user;
      this.notifyStatusChange();
    });

    this.notifyStatusChange();
  }

  private async retryOperation<T>(operation: () => Promise<T>): Promise<T> {
    let lastError;
    
    for (let attempt = 0; attempt < this.retryAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        if (error.code === 'permission-denied') {
          // Wait for authentication
          await new Promise<void>((resolve) => {
            const unsubscribe = auth.onAuthStateChanged((user) => {
              if (user) {
                unsubscribe();
                resolve();
              }
            });
          });
        }
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * (attempt + 1)));
      }
    }
    
    throw lastError;
  }

  private notifyStatusChange() {
    const update: RealtimeUpdate = {
      type: 'CONNECTION_STATUS',
      data: { ...this.connectionStatus }
    };

    this.statusSubscribers.forEach(subscriber => {
      try {
        subscriber(update);
      } catch (error) {
        console.error('Erro ao notificar subscriber:', error);
      }
    });
  }

  public subscribeToUpdates(table: string, callback: Subscriber): UnsubscribeFunction {
    if (!this.subscribers.has(table)) {
      this.subscribers.set(table, new Set());
      this.setupRealtimeListeners(table);
    }
    
    this.subscribers.get(table)?.add(callback);
    this.statusSubscribers.add(callback);

    // Notifica status inicial
    this.notifyStatusChange();

    return () => {
      this.subscribers.get(table)?.delete(callback);
      this.statusSubscribers.delete(callback);
      
      if (this.subscribers.get(table)?.size === 0) {
        this.cleanupRealtimeListeners(table);
      }
    };
  }

  private setupRealtimeListeners(table: string) {
    // Configura Supabase
    const channel = supabase.channel(`realtime:${table}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
        this.notifySubscribers(table, {
          type: payload.eventType as UpdateType,
          source: 'supabase',
          data: payload.new
        });
      })
      .subscribe();

    this.supabaseChannels.set(table, channel);

    // Configura Firebase
    const q = query(collection(db, table), orderBy('created_at', 'desc'), limit(100));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        this.notifySubscribers(table, {
          type: change.type,
          source: 'firebase',
          data: { id: change.doc.id, ...change.doc.data() }
        });
      });
    }, (error) => {
      console.error('Firebase snapshot error:', error);
      this.connectionStatus.firebase = false;
      this.notifyStatusChange();
    });

    this.firebaseUnsubscribers.set(table, unsubscribe);
  }

  private cleanupRealtimeListeners(table: string) {
    this.supabaseChannels.get(table)?.unsubscribe();
    this.supabaseChannels.delete(table);

    this.firebaseUnsubscribers.get(table)?.();
    this.firebaseUnsubscribers.delete(table);
  }

  private notifySubscribers(table: string, update: RealtimeUpdate) {
    this.subscribers.get(table)?.forEach(subscriber => {
      try {
        subscriber(update);
      } catch (error) {
        console.error('Erro ao notificar subscriber:', error);
      }
    });
  }

  public async syncData(table: string): Promise<void> {
    if (this.syncInProgress) return;
    
    try {
      this.syncInProgress = true;

      // Busca dados do Supabase
      const { data: supabaseData, error } = await supabase
        .from(table)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (!error && supabaseData?.length > 0) {
        this.notifySubscribers(table, {
          type: 'SYNC_COMPLETE',
          source: 'supabase',
          data: supabaseData
        });
        return;
      }

      // Se não houver dados no Supabase, tenta Firebase
      const q = query(collection(db, table), orderBy('created_at', 'desc'), limit(100));
      const snapshot = await getDocs(q);
      
      const firebaseData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.notifySubscribers(table, {
        type: 'SYNC_COMPLETE',
        source: 'firebase',
        data: firebaseData
      });

    } catch (error) {
      console.error('Erro ao sincronizar dados:', error);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }
}

export const realtimeService = new RealtimeService();